"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/hooks/useTranslation";
import { useEffect, useState, useRef } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
	XMarkIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
} from "@heroicons/react/24/outline";

interface Testimonial {
	id: number;
	name: string;
	year: string;
	text: string;
}

interface TestimonialModalProps {
	isOpen: boolean;
	onClose: () => void;
	testimonial: Testimonial | null;
}

function TestimonialModal({
	isOpen,
	onClose,
	testimonial,
}: TestimonialModalProps) {
	const isMobile = useIsMobile();
	const [isHydrated, setIsHydrated] = useState(false);

	useEffect(() => {
		setIsHydrated(true);
	}, []);

	if (!testimonial) return null;

	// Don't render animations until hydration is complete
	if (!isHydrated) {
		return (
			<>
				{isOpen && (
					<div
						className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
						onClick={onClose}
						onKeyDown={(e) => e.key === "Escape" && onClose()}
						role="dialog"
						aria-modal="true"
						tabIndex={-1}
					>
						<div
							className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => e.stopPropagation()}
							role="document"
						>
							{/* Header */}
							<div className="flex items-center justify-between p-6 border-b border-gray-200">
								<h2 className="text-2xl font-bold text-gray-900">
									{testimonial.name}
								</h2>
								<button
									type="button"
									onClick={onClose}
									className="p-2 hover:bg-gray-100 rounded-full transition-colors"
									aria-label="Close testimonial"
								>
									<XMarkIcon className="w-6 h-6 text-gray-500" />
								</button>
							</div>

							{/* Content */}
							<div className="p-6">
								<div className="flex items-center justify-center mb-6" role="img" aria-label="5 out of 5 stars">
									{[...Array(5)].map((_, i) => (
										<svg
											key={`modal-star-${testimonial.id}-${i}`}
											className="w-6 h-6 text-yellow-400 fill-current"
											viewBox="0 0 20 20"
											aria-hidden="true"
										>
											<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
										</svg>
									))}
								</div>

								<p className="text-gray-700 leading-relaxed text-center italic text-lg mb-6">
									&ldquo;{testimonial.text}&rdquo;
								</p>

								<div className="text-center">
									<p className="text-sm text-gray-500 mb-4">
										{testimonial.year}
									</p>
								</div>
							</div>
						</div>
					</div>
				)}
			</>
		);
	}

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={isMobile ? {} : { opacity: 0 }}
					animate={isMobile ? {} : { opacity: 1 }}
					exit={isMobile ? {} : { opacity: 0 }}
					className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
					onClick={onClose}
				>
					<motion.div
						initial={isMobile ? {} : { scale: 0.9, opacity: 0 }}
						animate={isMobile ? {} : { scale: 1, opacity: 1 }}
						exit={isMobile ? {} : { scale: 0.9, opacity: 0 }}
						className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Header */}
						<div className="flex items-center justify-between p-6 border-b border-gray-200">
							<h2 className="text-2xl font-bold text-gray-900">
								{testimonial.name}
							</h2>
							<button
								type="button"
								onClick={onClose}
								className={`p-2 rounded-full ${
									isMobile ? "" : "hover:bg-gray-100 transition-colors"
								}`}
								aria-label="Close testimonial"
							>
								<XMarkIcon className="w-6 h-6 text-gray-500" />
							</button>
						</div>

						{/* Content */}
						<div className="p-6">
							<div className="flex items-center justify-center mb-6" role="img" aria-label="5 out of 5 stars">
								{[...Array(5)].map((_, i) => (
									<svg
										key={`animated-modal-star-${testimonial?.id}-${i}`}
										className="w-6 h-6 text-yellow-400 fill-current"
										viewBox="0 0 20 20"
										aria-hidden="true"
									>
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
									</svg>
								))}
							</div>

							<p className="text-gray-700 leading-relaxed text-center italic text-lg mb-6">
								&ldquo;{testimonial.text}&rdquo;
							</p>

							<div className="text-center">
								<p className="text-sm text-gray-500 mb-4">{testimonial.year}</p>
							</div>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}

export default function TestimonialsSection() {
	const { t } = useTranslation();
	const [selectedTestimonial, setSelectedTestimonial] =
		useState<Testimonial | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isHydrated, setIsHydrated] = useState(false);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const isMobile = useIsMobile();
	const shouldAnimate = isHydrated && !isMobile;

	useEffect(() => {
		setIsHydrated(true);
	}, []);

	// Get testimonials with correct key structure
	const getTestimonials = (): Testimonial[] => {
		const testimonials: Testimonial[] = [];

		// Try to get up to 14 testimonials
		for (let i = 1; i <= 14; i++) {
			const name = t(`testimonials.student${i}.name`);
			const year = t(`testimonials.student${i}.year`);
			const text = t(`testimonials.student${i}.text`);

			// Only add if we have actual content (not just the key)
			if (
				name &&
				name !== `testimonials.student${i}.name` &&
				text &&
				text !== `testimonials.student${i}.text`
			) {
				testimonials.push({
					id: i,
					name,
					year,
					text,
				});
			}
		}

		return testimonials;
	};

	const testimonials = getTestimonials();

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.6,
			},
		},
	};

	const truncateText = (text: string, maxLength: number = 120) => {
		if (text.length <= maxLength) return text;
		return text.substring(0, maxLength).trim() + "...";
	};

	const handleTestimonialClick = (testimonial: Testimonial) => {
		setSelectedTestimonial(testimonial);
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedTestimonial(null);
	};

	const scrollToIndex = (index: number) => {
		if (scrollContainerRef.current) {
			const container = scrollContainerRef.current;
			const cardWidth = isMobile ? 280 : 320;
			const gap = isMobile ? 16 : 24;
			const scrollPosition = index * (cardWidth + gap);

			container.scrollTo({
				left: scrollPosition,
				behavior: "smooth",
			});
			setCurrentIndex(index);
		}
	};

	const handlePrevious = () => {
		const newIndex =
			currentIndex > 0 ? currentIndex - 1 : testimonials.length - 1;
		scrollToIndex(newIndex);
	};

	const handleNext = () => {
		const newIndex =
			currentIndex < testimonials.length - 1 ? currentIndex + 1 : 0;
		scrollToIndex(newIndex);
	};

	// Don't render if no testimonials found
	if (testimonials.length === 0) {
		return null;
	}

	return (
		<section className="py-10 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
			{/* Background Elements */}
			<div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-cyan/5 to-transparent"></div>

			<div className="max-w-7xl mx-auto relative z-10">
				<motion.div
					initial={shouldAnimate ? { opacity: 0, y: 50 } : false}
					whileInView={shouldAnimate ? { opacity: 1, y: 0 } : undefined}
					transition={shouldAnimate ? { duration: 0.8 } : undefined}
					viewport={{ once: true }}
					className="text-center mb-16"
				>
					<h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
						{t("testimonials.title")}
					</h2>

					<div className="max-w-3xl mx-auto">
						<p className="text-xl text-gray-700 max-w-3xl mx-auto">
							{t("testimonials.subtitle")}
						</p>
					</div>
				</motion.div>

				{/* Carousel Container with Navigation */}
				<div className="relative">
					{/* Navigation Arrows */}
					<button
						type="button"
						onClick={handlePrevious}
						className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110"
						aria-label="Previous testimonial"
					>
						<ChevronLeftIcon className="w-6 h-6 text-gray-700" aria-hidden="true" />
					</button>

					<button
						type="button"
						onClick={handleNext}
						className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110"
						aria-label="Next testimonial"
					>
						<ChevronRightIcon className="w-6 h-6 text-gray-700" aria-hidden="true" />
					</button>

					{/* Left Gradient Overlay */}
					<div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none"></div>

					{/* Right Gradient Overlay */}
					<div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none"></div>

					{/* Carousel */}
					<motion.div
						ref={scrollContainerRef}
						variants={shouldAnimate ? containerVariants : undefined}
						initial={shouldAnimate ? "hidden" : false}
						whileInView={shouldAnimate ? "visible" : undefined}
						viewport={{ once: true }}
						role="region"
						aria-label="Student testimonials"
						aria-roledescription="carousel"
						className="flex gap-4 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide px-12"
						style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
					>
						{testimonials.map((testimonial) => (
							<motion.div
								key={testimonial.id}
								variants={shouldAnimate ? itemVariants : undefined}
								onClick={() => handleTestimonialClick(testimonial)}
								className="bg-white/90 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20 hover:bg-white/95 transition-all duration-300 group flex-shrink-0 snap-center relative shadow-lg cursor-pointer hover:shadow-xl"
								style={{
									width: isMobile ? "280px" : "320px",
									height: "280px",
									minWidth: isMobile ? "280px" : "320px",
								}}
							>
								<div className="flex items-center justify-center mb-4" role="img" aria-label="5 out of 5 stars">
									{[...Array(5)].map((_, i) => (
										<svg
											key={`star-${testimonial.id}-${i}`}
											className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 fill-current"
											viewBox="0 0 20 20"
											aria-hidden="true"
										>
											<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
										</svg>
									))}
								</div>

								<div className="flex-1 flex flex-col justify-between h-full">
									<p className="text-gray-700 leading-relaxed text-center italic text-sm md:text-base mb-4 overflow-hidden">
										&ldquo;{truncateText(testimonial.text)}&rdquo;
									</p>

									<div className="text-center mt-auto">
										<h3 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">
											{testimonial.name}
										</h3>
										<p className="text-xs md:text-sm text-gray-500 mb-4">
											{testimonial.year}
										</p>
									</div>
								</div>

								<div className="absolute top-4 right-4 text-3xl md:text-4xl text-gray-200 group-hover:text-gray-300 transition-colors duration-300">
									&rdquo;
								</div>
							</motion.div>
						))}
					</motion.div>
				</div>
			</div>

			<TestimonialModal
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				testimonial={selectedTestimonial}
			/>
		</section>
	);
}
